class ScrollStory extends HTMLElement {
  constructor() {
    super();
    this.cards = [];
    this.count = parseInt(this.dataset.cardCount, 10) || 0;
    this.rafId = null;
    this.state = []; // per-card current { x, y, rot } — lerped toward target each frame
    this.reducedMotion = false;

    this.loop = this.loop.bind(this);

    // Small per-card variation so cards don't all rotate identically
    this.rotations = Array.from({ length: this.count }, (_, i) => {
      const dir = i % 2 === 0 ? -1 : 1;
      return dir * (6 + (i % 3) * 4);
    });
  }

  connectedCallback() {
    this.cards = Array.from(this.querySelectorAll('.ss_card'));
    if (this.cards.length === 0) return;

    this.state = this.cards.map(() => ({ x: 0, y: 0, rot: 0 }));
    this.reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    if (this.reducedMotion) {
      // Accessible fallback: plain static grid, no scroll-linked motion.
      // See the .ss-reduced-motion rules in the CSS file.
      this.classList.add('ss-reduced-motion');
      return;
    }

    this.rafId = window.requestAnimationFrame(this.loop);
  }

  disconnectedCallback() {
    if (this.rafId) {
      window.cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  loop() {
    this.applyPinned();
    this.rafId = window.requestAnimationFrame(this.loop);
  }

  // One shared pinned, scroll-linked story for every screen size. The
  // math is already resolution-independent — it reads the live viewport
  // height and this element's own scroll position every frame — so the
  // same code drives desktop and mobile identically. Layout differences
  // (image on top vs. side-by-side, card size/position) are handled in
  // CSS via media queries; this function doesn't need to know about them.
  applyPinned() {
  const rect = this.getBoundingClientRect();
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  const scrollableDistance = this.offsetHeight - viewportHeight;
  if (scrollableDistance <= 0) return;

  const scrolled = -rect.top;
  const rawProgress = Math.max(0, Math.min(1, scrolled / scrollableDistance));

  // Smooth the progress itself so fast scrolling doesn't cause cards
  // to jump to wildly different targets frame-to-frame — this keeps
  // relative card spacing constant regardless of scroll speed.
  if (this.smoothedProgress === undefined) this.smoothedProgress = rawProgress;
  const progressEase = 0.12; // lower = smoother/slower catch-up, higher = snappier
  this.smoothedProgress += (rawProgress - this.smoothedProgress) * progressEase;

  const totalProgress = this.smoothedProgress;

  const n = this.cards.length;
  const segment = 1 / n;
  const travel = viewportHeight * 0.7;
  const ease = 0.045;

  this.cards.forEach((card, i) => {
    const center = (i + 0.5) * segment;
    const diff = totalProgress - center;
    const t = Math.max(-1.3, Math.min(1.3, diff / segment));

    const targetX = t * -14;
    const targetY = -t * travel;
    const targetRot = (this.rotations[i] || 0) + t * 12;

    const s = this.state[i];
    s.x += (targetX - s.x) * ease;
    s.y += (targetY - s.y) * ease;
    s.rot += (targetRot - s.rot) * ease;

    card.style.transform =
      `translate(-50%, -50%) translate(${s.x.toFixed(1)}px, ${s.y.toFixed(1)}px) rotate(${s.rot.toFixed(2)}deg)`;
    card.style.zIndex = Math.round((1 - Math.abs(t)) * 100).toString();
  });
}
}

if (!customElements.get('scroll-story')) {
  customElements.define('scroll-story', ScrollStory);
}
